import { ContactAPI } from '@business/api/contact';
import { TagAPI } from '@business/api/tag';
import { PageQuery } from '@business/entities/common';
import { ContactId, ContactRequest, ContactResponse } from '@business/entities/contact';
import { OrgId } from '@business/entities/organization';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showContactRemoveModal } from '@modules/people/DeleteContactModal';
import { switchMap, tap } from 'rxjs/operators';

export class ContactBloc extends BaseBloc {
  private readonly contactRepo = new Repository<ContactResponse>({
    getItemId: contact => contact.id,
    pagenated: true,
  });

  readonly contacts$ = this.contactRepo.items$;
  readonly selectContact = this.contactRepo.selectItem;

  onReset() {
    this.contactRepo.reset();
  }

  fetchContactsPaginated = (options: PageQuery) => {
    return ContactAPI.fetchContactsPaginated(options);
  };

  fetchContacts = (options: { search?: string; orgId?: OrgId } = {}) => {
    return ContactAPI.fetchContacts(options).pipe(this.contactRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_contacts_error') }));
  };

  fetchContact = (id: ContactId) => {
    return ContactAPI.fetchContact(id).pipe(
      this.contactRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_contacts_error') }),
    );
  };
  fetchContactOrganization = (id: ContactId) => {
    return ContactAPI.fetchContactOrganization(id).pipe(
      handleMessage({ error: requestMessage('fetch_contacts_error') }),
    );
  };

  createContact = (contact: ContactRequest) => {
    return ContactAPI.createContact(contact).pipe(
      this.contactRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_contact'),
      }),
    );
  };

  updateContact = (id: ContactId, contact: ContactRequest) => {
    return ContactAPI.updateContact(id, contact).pipe(
      this.contactRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_contact'),
      }),
    );
  };

  deleteContact = (id: ContactId) => {
    return showContactRemoveModal({}).pipe(
      switchMap(removeEmails => {
        return ContactAPI.deleteContact(id, removeEmails).pipe(
          tap(() => {
            if (removeEmails) {
              document.location.reload();
            }
          }),
        );
      }),
      this.contactRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_contact'),
      }),
    );
  };

  getDataByPhone = (phone: string) => {
    return ContactAPI.getDataByPhone(phone).pipe(this.contactRepo.ops.upsertOne(item => ({ item })));
  };

  attachTags = (id: ContactId, tags: string[]) => {
    return TagAPI.attachTags(id, 'App\\Models\\Contact', tags).pipe(
      this.contactRepo.ops.updateOne(tags => ({
        id: id,
        changes: { tags },
      })),
    );
  };

  detachTag = (id: ContactId, tag: string) => {
    return TagAPI.detachTag(id, 'App\\Models\\Contact', tag).pipe(
      this.contactRepo.ops.updateOne(tags => ({
        id: id,
        changes: { tags },
      })),
    );
  };
}
